import React from 'react';

import styled from '@emotion/styled';
import {AnimatePresence, motion} from 'framer-motion';
import {groupBy} from 'lodash';
import {observer} from 'mobx-react';
import {DeviceType, NetworkState} from 'prolink-connect/lib/types';

import type {AppStore, DeviceStore} from 'src/shared/store';
import withStore from 'src/utils/withStore';
import ConnectingSplash from 'ui/components/ConnectingSplash';
import ConnectionError from 'ui/components/ConnectionError';
import BeatCounter from 'ui/components/device/BeatCounter';
import BpmIndicator from 'ui/components/device/BpmIndicator';
import DbStateIndicator from 'ui/components/device/DbStateIndicator';
import DeviceInfo from 'ui/components/device/DeviceInfo';
import Metadata from 'ui/components/device/Metadata';
import PlayState from 'ui/components/device/PlayState';
import IconCdj from 'ui/icons/cdj';
import IconDjm from 'ui/icons/djm';
import IconRekordbox from 'ui/icons/rekordbox';

const sortById = (a: DeviceStore, b: DeviceStore) => a.device.id - b.device.id;

type Props = {
  store: AppStore;
};

const Devices = observer(({store}: Props) => {
  const deviceList = [...store.devices.values()];
  const deviceMap = groupBy(deviceList, d => d.device.type);
  const otherDevices = [
    ...(deviceMap[DeviceType.Mixer] ?? []),
    ...(deviceMap[DeviceType.Rekordbox] ?? []),
  ];

  return (
    <>
      <AnimatePresence initial={false}>
        {deviceMap[DeviceType.CDJ]?.sort(sortById).map(deviceStore => {
          const {device, state} = deviceStore;
          return (
            <Device key={device.id}>
              <Indicator>
                <PlayerId onair={Boolean(state?.isOnAir)}>
                  {device.id.toString().padStart(2, '0')}
                </PlayerId>
                <IconCdj />
              </Indicator>
              <Status>
                <TopBar>
                  <StatusBar>
                    <PlayState playState={state?.playState} />
                    <BeatCounter deviceId={device.id} />
                  </StatusBar>
                  <DeviceInfo device={device} />
                </TopBar>
                <StatusBar>
                  <BpmIndicator pitch={state?.sliderPitch} bpm={state?.trackBPM} />
                  <DbStateIndicator deviceId={device.id} />
                  <Metadata deviceId={device.id} />
                </StatusBar>
              </Status>
            </Device>
          );
        })}
        <SmallDeviceList key="smallDevices">
          <AnimatePresence initial={false}>
            {otherDevices.map(({device}) => (
              <SmallDevice key={device.id}>
                {device.type === DeviceType.Mixer && <IconDjm />}
                {device.type === DeviceType.Rekordbox && <IconRekordbox />}
                <ConnectedTag>Connected</ConnectedTag>
                <DeviceInfo device={device} />
              </SmallDevice>
            ))}
          </AnimatePresence>
        </SmallDeviceList>
      </AnimatePresence>
      {(store.networkState === NetworkState.Online ||
        (store.networkState === NetworkState.Connected && deviceList.length === 0)) && (
        <ConnectingSplash key="splash" />
      )}
      {store.networkState === NetworkState.Failed && <ConnectionError key="error" />}
    </>
  );
});

const Device = styled(motion.div)`
  display: grid;
  grid-template-columns: max-content 1fr;
  grid-gap: 0.5rem;
  padding: 1rem;
  border-bottom: 1px solid ${p => p.theme.border};
`;

Device.defaultProps = {
  layout: true,
  transition: {duration: 0.2},
  initial: {y: 10, opacity: 0},
  animate: {y: 0, opacity: 1},
  exit: {opacity: 0},
};

const Indicator = styled('div')`
  display: grid;
  grid-template-rows: 22px 48px;
  grid-gap: 0.5rem;
  padding-right: 0.5rem;
  border-right: 1px solid ${p => p.theme.border};

  svg {
    color: ${p => p.theme.cdjStatus.icon};
  }
`;

const Status = styled('div')`
  display: grid;
  grid-template-rows: 22px 48px;
  grid-gap: 0.5rem;
`;

const TopBar = styled('div')`
  display: grid;
  grid-template-columns: 1fr max-content;
  grid-gap: 0.5rem;
`;

const StatusBar = styled('div')`
  display: inline-grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  grid-gap: 0.5rem;
`;

const SmallDeviceList = styled('div')`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  grid-gap: 2rem;
  padding: 1rem;
  border-bottom: 1px solid ${p => p.theme.border};

  &:empty {
    display: none;
  }
`;

const SmallDevice = styled(motion.div)`
  display: grid;
  grid-gap: 0.25rem 0.5rem;
  grid-template: 1fr max-content / repeat(2, max-content);
  align-items: center;

  > svg:first-of-type {
    grid-row: 1 / span 2;
  }

  svg {
    color: ${p => p.theme.cdjStatus.icon};
  }
`;

SmallDevice.defaultProps = {
  transition: {duration: 0.2},
  initial: {x: 10, opacity: 0},
  animate: {x: 0, opacity: 1},
  exit: {opacity: 0},
};

const ConnectedTag = styled('div')`
  text-transform: uppercase;
  font-size: 0.7rem;
  font-weight: 600;
  display: grid;
  grid-template-columns: max-content 1fr;
  grid-gap: 0.25rem;
  align-items: center;

  &:before {
    content: '';
    display: block;
    height: 0.5rem;
    width: 0.5rem;
    border-radius: 50%;
    background: ${p => p.theme.critical};
  }
`;

const PlayerId = styled('div')<{onair: boolean}>`
  font-size: 0.825rem;
  line-height: 22px;
  width: 34px;
  font-weight: 700;
  background: ${p => (p.onair ? p.theme.critical : '#3b434b')};
  text-align: center;
  align-items: center;
  color: #fff;
  border-radius: 3px;
  overflow: hidden;
`;

export default withStore(Devices);
